import { createFileRoute, useNavigate } from '@tanstack/react-router'
import i18next from 'i18next'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { DetectionComparisonPage } from '@/components/DetectionComparison/DetectionComparisonPage'
import { requirePermission } from '@/utils/auth'

export type DetectionComparisonSearch = {
  first?: number
  second?: number
}

const parseDriveId = (raw: unknown): number | undefined => {
  const value = typeof raw === 'string' ? Number(raw) : raw
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined
}

export const Route = createFileRoute('/detection-comparison')({
  beforeLoad: () => requirePermission('detection_matching', 'read'),
  validateSearch: (raw: Record<string, unknown>): DetectionComparisonSearch => {
    const out: DetectionComparisonSearch = {}
    const first = parseDriveId(raw.first)
    const second = parseDriveId(raw.second)
    if (first !== undefined) out.first = first
    if (second !== undefined) out.second = second
    return out
  },
  component: DetectionComparisonRoute,
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('title', { ns: 'detectionComparison' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoad', { ns: 'detectionComparison' })}
        message={error.message || i18next.t('unableToLoad', { ns: 'detectionComparison' })}
      />
    </PageShell>
  ),
})

function DetectionComparisonRoute() {
  const { first, second } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <DetectionComparisonPage
      firstDriveId={first}
      secondDriveId={second}
      onDrivesChange={(firstDriveId, secondDriveId) =>
        navigate({ search: { first: firstDriveId, second: secondDriveId }, replace: true })
      }
    />
  )
}
